import {
  traceQuery,
  tracesInfiniteQuery,
  useArchiveTraceMutation,
  useRestoreTraceMutation,
} from "@common/api/traces"
import { pagedRows } from "@common/api/utils"
import type { TracePublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import { GridTable } from "@common/components/ui/grid-table"
import { Skeleton } from "@common/components/ui/skeleton"
import { formatDateLabel } from "@common/lib/date-utils"
import { handleError } from "@common/utils"
import { useQueries, useSuspenseInfiniteQuery } from "@tanstack/react-query"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { MessagesSquare, Pin, Plus } from "lucide-react"
import { Suspense } from "react"
import { z } from "zod"
import { GroupedTable } from "@/components/layout/grouped-table"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListPage } from "@/components/layout/list-page"
import {
  type ListColumns,
  ListTableCell,
  ListTableHeader,
  ListTableLink,
  ListTableRow,
  ListTableText,
} from "@/components/layout/list-table"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { LoadMoreSentinel } from "@/components/layout/load-more-sentinel"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnLayout } from "@/hooks/use-column-layout"
import useCustomToast from "@/hooks/use-custom-toast"
import type { ColumnLayout } from "@/lib/column-widths"
import { ChatActionsMenu } from "./-components/chat-actions-menu"
import { EditableTraceTitle } from "./-components/editable-trace-title"
import { usePinnedTraces } from "./-lib/pinned-traces"

type Scope = "active" | "archived"

const filterSchema = z.object({
  search: z.string().optional().catch(""),
  scope: z.enum(["active", "archived"]).optional().catch("active"),
})

const chatsListQuery = (search: string, scope: Scope) =>
  tracesInfiniteQuery({
    search: search || null,
    order_by: "updated_at",
    order_direction: "desc",
    scope,
  })

export const Route = createFileRoute("/_layout/agent/chats")({
  staticData: { breadcrumb: "Chats" },
  validateSearch: (search) => filterSchema.parse(search),
  loaderDeps: ({ search: { search = "", scope = "active" } }) => ({
    search,
    scope,
  }),
  loader: ({ context: { queryClient }, deps: { search, scope } }) =>
    queryClient.ensureInfiniteQueryData(chatsListQuery(search, scope)),
  component: ChatsPage,
})

const SCOPES: Scope[] = ["active", "archived"]

type Slot = "title" | "updated" | "actions"

const COLUMNS: ListColumns<Slot> = {
  slots: ["title", "updated", "actions"],
  end: ["actions"],
  muted: ["updated"],
}

const LAYOUT: ColumnLayout<Slot> = {
  id: "agent-chats",
  widths: { title: 12, updated: 9, actions: 4 },
  fill: "title",
  pinned: ["actions"],
}

const HEAD_LABELS: Record<Slot, string | null> = {
  title: "Chat",
  updated: "Last message",
  actions: null,
}

function ChatsPage(): React.ReactElement {
  const { search = "", scope = "active" } = Route.useSearch()

  return (
    <ListPage
      toolbar={<Toolbar search={search} scope={scope} />}
      detail={<Outlet />}
    >
      <Suspense fallback={<ChatListSkeleton />}>
        <ChatResults search={search} scope={scope} />
      </Suspense>
    </ListPage>
  )
}

function Toolbar({
  search,
  scope,
}: {
  search: string
  scope: Scope
}): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={SCOPES}
          tab={scope}
          label={(option) => (option === "archived" ? "Archived" : "Chats")}
          onSelect={(next) =>
            navigate({
              search: (previous) => ({
                ...previous,
                scope: next === "archived" ? next : undefined,
              }),
            })
          }
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) =>
              navigate({
                search: (previous) => ({
                  ...previous,
                  search: next || undefined,
                }),
              })
            }
            placeholder="Search your chats…"
          />
          {scope === "active" && (
            <Button size="sm" onClick={() => navigate({ to: "/agent" })}>
              <Plus />
              New chat
            </Button>
          )}
        </>
      }
    />
  )
}

function ChatResults({
  search,
  scope,
}: {
  search: string
  scope: Scope
}): React.ReactElement {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useSuspenseInfiniteQuery(chatsListQuery(search, scope))
  const traces = pagedRows(data.pages, (trace) => trace.id)
  const { pinnedTraceIds, isPinned } = usePinnedTraces()
  const layout = useColumnLayout(COLUMNS.slots, LAYOUT)
  const pinnedQueries = useQueries({
    queries:
      scope === "active"
        ? pinnedTraceIds.map((id) => ({
            ...traceQuery({ id }),
            retry: false,
          }))
        : [],
  })
  const normalizedSearch = search.trim().toLocaleLowerCase()
  const pinnedTraces = pinnedQueries
    .map((query) => query.data)
    .filter(
      (trace): trace is TracePublic =>
        trace !== undefined && trace.status === "active",
    )
    .filter(
      (trace) =>
        !normalizedSearch ||
        trace.title.toLocaleLowerCase().includes(normalizedSearch),
    )

  if (traces.length === 0 && pinnedTraces.length === 0) {
    return <ListEmpty icon={MessagesSquare} {...emptyCopy(search, scope)} />
  }

  const listedTraces =
    scope === "active" ? traces.filter((trace) => !isPinned(trace.id)) : traces
  const style = { gridTemplateColumns: layout.template }
  const header = (
    <ListTableHeader
      columns={{ ...COLUMNS, slots: layout.slots }}
      arrange={layout.arrange}
    >
      {(slot) => HEAD_LABELS[slot]}
    </ListTableHeader>
  )

  return (
    <>
      {pinnedTraces.length > 0 ? (
        <GroupedTable
          rows={[...pinnedTraces, ...listedTraces]}
          style={style}
          header={header}
          group="pinned"
          subgroup={undefined}
          groupOf={(trace) =>
            isPinned(trace.id)
              ? { key: "pinned", label: "Pinned", order: 0 }
              : { key: "all", label: "All chats", order: 1 }
          }
          renderRows={(rows) => (
            <ChatRows traces={rows} slots={layout.slots} scope={scope} />
          )}
          sticky
          hideCount
        />
      ) : (
        <GridTable style={style}>
          {header}
          <ChatRows traces={listedTraces} slots={layout.slots} scope={scope} />
        </GridTable>
      )}
      <LoadMoreSentinel
        noun="chats"
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isError={isFetchNextPageError}
        fetchNextPage={fetchNextPage}
      />
    </>
  )
}

function emptyCopy(
  search: string,
  scope: Scope,
): { title: string; body: string } {
  if (search) {
    return {
      title: "No matching chats",
      body: "Try a different search term.",
    }
  }
  if (scope === "archived") {
    return {
      title: "No archived chats",
      body: "Chats you archive will appear here.",
    }
  }
  return {
    title: "No chats yet",
    body: "Chats with the agent will appear here.",
  }
}

function ChatRows({
  traces,
  slots,
  scope,
}: {
  traces: TracePublic[]
  slots: Slot[]
  scope: Scope
}): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const { isPinned, togglePinned, removePinned } = usePinnedTraces()
  const archiveMutation = useArchiveTraceMutation()
  const restoreMutation = useRestoreTraceMutation()
  const showToast = useCustomToast()
  const columns = { ...COLUMNS, slots }

  return (
    <>
      {traces.map((trace) => (
        <ListTableRow
          key={trace.id}
          onClick={(event) => {
            if (event.target instanceof HTMLInputElement) return
            navigate({
              to: "/agent/$id",
              params: { id: trace.id },
              search: { from: "chats" },
            })
          }}
        >
          <EditableTraceTitle
            trace={trace}
            className="col-span-full my-0.5"
            renderResting={(rename, title) => {
              const cells: Record<Slot, React.ReactElement> = {
                title: (
                  <ListTableCell key="title" columns={columns} slot="title">
                    {isPinned(trace.id) && (
                      <Pin className="size-3.5 shrink-0 fill-current" />
                    )}
                    <ListTableLink
                      to="/agent/$id"
                      params={{ id: trace.id }}
                      search={{ from: "chats" }}
                      className="text-foreground hover:no-underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {title}
                    </ListTableLink>
                  </ListTableCell>
                ),
                updated: (
                  <ListTableCell key="updated" columns={columns} slot="updated">
                    <ListTableText>
                      {formatDateLabel(trace.updated_at)}
                    </ListTableText>
                  </ListTableCell>
                ),
                actions: (
                  <ListTableCell
                    key="actions"
                    columns={columns}
                    slot="actions"
                    reveal
                    onClick={(event) => event.stopPropagation()}
                  >
                    <ChatActionsMenu
                      trace={trace}
                      pinned={isPinned(trace.id)}
                      onTogglePinned={() => togglePinned(trace.id)}
                      archived={scope === "archived"}
                      onToggleArchived={() => {
                        const archived = scope !== "archived"
                        const mutation = archived
                          ? archiveMutation
                          : restoreMutation
                        mutation.mutate(
                          { id: trace.id },
                          {
                            onSuccess: () => {
                              if (archived) removePinned(trace.id)
                              showToast(
                                archived ? "Chat archived" : "Chat restored",
                                archived
                                  ? "The chat was moved to Archived."
                                  : "The chat was restored to your chat history.",
                                "success",
                              )
                            },
                            onError: (error) => handleError(error, showToast),
                          },
                        )
                      }}
                      onRename={rename}
                      onDelete={() =>
                        navigate({
                          to: "/agent/chats/delete/$id",
                          params: { id: trace.id },
                          search: true,
                        })
                      }
                    />
                  </ListTableCell>
                ),
              }
              return <>{slots.map((slot) => cells[slot])}</>
            }}
          />
        </ListTableRow>
      ))}
    </>
  )
}

function ChatListSkeleton(): React.ReactElement {
  return (
    <div className="space-y-2 px-2 py-3">
      {[1, 2, 3, 4, 5, 6].map((index) => (
        <Skeleton key={index} className="h-9 w-full rounded-md" />
      ))}
    </div>
  )
}
